import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft, Mail } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";

const errToString = (detail) => {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(detail);
};

const LoginPage = () => {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/dashboard";

  // step 1 — credentials, step 2 — OTP
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const goAfterLogin = (role) => {
    if (role === "cashier") navigate("/cashier");
    else if (role === "production") navigate("/production");
    else if (role === "releasing") navigate("/releasing");
    else if (["super_admin", "front_office", "accounting"].includes(role)) navigate("/admin");
    else navigate(next);
  };

  // OTP step disabled: log in directly with email + password.
  const requestOtp = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      await refresh();
      toast.success("Welcome back!");
      goAfterLogin(data?.role);
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      await refresh();
      toast.success("Welcome back!");
      goAfterLogin(data?.role);
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail) || "Invalid code");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/request-otp", { email, password });
      toast.success("New code sent");
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail) || "Failed to resend");
    } finally { setResending(false); }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-10" data-testid="login-page">
      <div className="w-full max-w-md pav-card">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-lg" />
          <div>
            <h1 className="font-display text-2xl font-extrabold text-[#0B1E3F]">
              {step === 1 ? "Welcome back" : "Verify your email"}
            </h1>
            <p className="text-sm text-neutral-500">
              {step === 1
                ? "Sign in to your Print Ad account"
                : <>We sent a code to <strong>{email}</strong></>}
            </p>
          </div>
        </div>

        {step === 1 ? (
          <>
            <form onSubmit={requestOtp} className="space-y-3">
              <input className="pav-input" type="email" placeholder="Email"
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input className="pav-input" type="password" placeholder="Password"
                data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)} required />
              <div className="flex justify-end -mt-1">
                <button type="button" onClick={() => setForgotOpen(true)}
                  className="text-xs font-semibold text-[#06B6D4] hover:underline"
                  data-testid="login-forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Send login code
              </button>
              <p className="text-[11px] text-neutral-500 text-center">For your security, we&apos;ll email you a 6-digit code.</p>
            </form>
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-[#E2E8F0]" />
              <span className="text-xs uppercase tracking-wider text-neutral-400">or</span>
              <div className="h-px flex-1 bg-[#E2E8F0]" />
            </div>
            <button onClick={handleGoogle} className="w-full btn-outline inline-flex items-center justify-center gap-2" data-testid="login-google-button">
              <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.8-8 19.8-20 0-1.2-.1-2.3-.3-3.5z"/><path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.1z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2L31 33c-2 1.4-4.4 2.2-7 2.2-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.3 5.8l6.5 5.3C41.4 35.9 44 30.5 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
              Continue with Google
            </button>
            <p className="text-sm text-neutral-600 mt-6 text-center">
              New here? <Link to="/register" className="text-[#0B1E3F] font-semibold hover:underline" data-testid="login-register-link">Create an account</Link>
            </p>
          </>
        ) : (
          <form onSubmit={verifyOtp} className="space-y-4">
            <div className="rounded-xl bg-[#F4F6FB] border border-[#E2E8F0] p-3 flex items-center gap-2.5 text-sm">
              <Mail className="h-4 w-4 text-[#06B6D4]" />
              <span>Enter the 6-digit code from your email. Valid for 10 minutes.</span>
            </div>
            <input
              type="text" maxLength={6} inputMode="numeric"
              className="pav-input tracking-[0.5em] text-center text-2xl font-mono"
              placeholder="••••••"
              data-testid="login-otp-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              autoFocus required />
            <button type="submit" disabled={loading || code.length !== 6}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-verify-otp-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Verify &amp; sign in
            </button>
            <div className="flex justify-between items-center text-xs">
              <button type="button" onClick={() => { setStep(1); setCode(""); }}
                className="inline-flex items-center gap-1 text-neutral-600 hover:text-[#0B1E3F]"
                data-testid="login-back-button">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
              <button type="button" onClick={resend} disabled={resending}
                className="text-[#06B6D4] font-semibold hover:underline disabled:opacity-50"
                data-testid="login-resend-button">
                {resending ? "Sending…" : "Resend code"}
              </button>
            </div>
          </form>
        )}
      </div>
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} defaultEmail={email} />
    </div>
  );
};

export default LoginPage;
